/**
 * EntitySelection Component
 *
 * ─── UX Flow: Entity Selection (Intent + Structure) ─────────────────────────
 *
 * 1. Discovery Phase (Async with polling)
 *    - POST /connections/{id}/entities/discover → kicks off background pipeline
 *    - Poll GET /connections/{id}/entities/status every 1.5s
 *    - Show spinner with real-time status messages from the server
 *    - When status === 'complete', extract result and proceed
 *
 * 2. Intent Selection (if presets available)
 *    - Show preset cards (Sales, Service, Marketing, Custom)
 *    - Presets preselect entities but don't affect grouping
 *
 * 3. Structured Entity Selection
 *    - Entities grouped structurally (Core, Standard, Custom, Advanced)
 *    - All groups always visible (no toggle)
 *    - Core expanded by default, others collapsed
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Text,
  Input,
  Button,
  Checkbox,
  Spinner,
  tokens,
  Card,
  Badge,
  Field,
  Select,
  Radio,
  RadioGroup,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  SearchRegular,
  CheckmarkCircleRegular,
  ChevronDownRegular,
  ChevronRightRegular,
  DatabaseRegular,
  OptionsRegular,
} from '@fluentui/react-icons';
import { connectionsService } from '../../api/connectionsService';
import { defaultEntitySelection, needsPollingMetadataRefresh, reconcilePollingSelections, prepareEntitySelectionSave, applyEntitySelectionDraft, entitySelectionIssues, unavailableEntitySelections, applyPostgreSQLSyncStrategy, inferPostgreSQLSyncStrategy, type PostgreSQLSyncStrategy } from './entitySyncConfig';
import { refreshEntityDiscovery } from './refreshEntityDiscovery';
import { routeDiscoveredEntities } from './entityCardinalityRouting';
import { PostgreSQLPollingOptions } from './PostgreSQLPollingOptions';
import { PostgreSQLCDCSetupPanel } from './PostgreSQLCDCSetupPanel';
import { IncrementalRetentionOptions } from './IncrementalRetentionOptions';
import type { NormalizedRetentionPolicy } from './NormalizedRetentionField';
import { postgreSQLCDCVerificationFeedback } from './postgreSQLCDCSetup';
import type {
  EntityDiscoveryItem,
  EntityPreset,
  EntityGroup,
  EntityDiscoveryStatusResponse,
  EntityRelationship,
  SelectedEntity,
  PostgreSQLCDCConfig,
  PostgreSQLCDCDiscovery,
} from '../../api/types';
import { indexEntityRelationships } from './entityRelationships';
import { TableInspectorDrawer } from './TableInspectorDrawer';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EntitySelectionProps {
  connectionId: string;
  /** Called after entities are saved successfully */
  onComplete: () => void;
  /** Exit editing without saving the local selection. */
  onCancel?: () => void;
  /** Optional: pre-selected entities from metadata */
  initialSelected?: SelectedEntity[];
  normalizedRetention?: NormalizedRetentionPolicy;
  /** Initial setup only; edit mode always presents selection. */
  autoSelectSingleEntity?: boolean;
}

type Phase = 'discovering' | 'intent' | 'selection';

/** Polling interval in ms */
const POLL_INTERVAL = 1500;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const EntitySelection: React.FC<EntitySelectionProps> = ({
  connectionId,
  onComplete,
  onCancel,
  initialSelected,
  normalizedRetention,
  autoSelectSingleEntity = false,
}) => {
  // ─── Data state ────────────────────────────────────────────────────
  const [groups, setGroups] = useState<EntityGroup[]>([]);
  const [presets, setPresets] = useState<EntityPreset[]>([]);
  const [relationships, setRelationships] = useState<EntityRelationship[]>([]);
  const [cdcDiscovery, setCDCDiscovery] = useState<PostgreSQLCDCDiscovery | null>(null);
  const [cdcConfig, setCDCConfig] = useState<PostgreSQLCDCConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [discoveryWarning, setDiscoveryWarning] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshPurpose, setRefreshPurpose] = useState<'tables' | 'cdc' | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [cdcVerificationError, setCDCVerificationError] = useState<string | null>(null);
  const refreshRef = useRef<AbortController | null>(null);
  const autoRefreshAttempted = useRef(false);

  // ─── Discovery state ──────────────────────────────────────────────
  const [phase, setPhase] = useState<Phase>('discovering');
  const [discoveryMessage, setDiscoveryMessage] = useState('Starting discovery…');
  const [discoveryEntityCount, setDiscoveryEntityCount] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortRef = useRef(false);

  // ─── UX state ──────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['core']));
  const [inspectedEntity, setInspectedEntity] = useState<EntityDiscoveryItem | null>(null);
  const [focusIssue, setFocusIssue] = useState<string | null>(null);
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const initialSyncStrategy = inferPostgreSQLSyncStrategy(initialSelected ?? []);
  const [syncStrategy, setSyncStrategy] = useState<PostgreSQLSyncStrategy>(initialSyncStrategy);
  const [pendingSyncStrategy, setPendingSyncStrategy] = useState<PostgreSQLSyncStrategy | null>(null);
  const pollingDraftsRef = useRef(new Map((initialSelected ?? [])
    .filter(selection => selection.sync_mode !== 'cdc')
    .map(selection => [selection.entity_name, selection])));

  // ─── Selection state: map of entity name → config ──────────────────
  const [selected, setSelected] = useState<Map<string, SelectedEntity>>(() => {
    const map = new Map<string, SelectedEntity>();
    if (initialSelected) {
      initialSelected.forEach((e) => map.set(e.entity_name, e));
    }
    return map;
  });

  // ─── All entities flattened (for preset matching) ──────────────────
  const allEntities = useMemo(() => {
    return groups.flatMap((g) => g.entities);
  }, [groups]);
  const relationshipIndex = useMemo(
    () => indexEntityRelationships(allEntities, relationships),
    [allEntities, relationships],
  );

  const selectionForCurrentStrategy = useCallback((entity: EntityDiscoveryItem, previous?: SelectedEntity) => {
    const polling = pollingDraftsRef.current.get(entity.name) ?? previous ?? defaultEntitySelection(entity);
    return syncStrategy === 'cdc' ? applyPostgreSQLSyncStrategy(
      new Map([[entity.name, polling]]), [entity], 'cdc', pollingDraftsRef.current,
    ).get(entity.name)! : polling;
  }, [syncStrategy]);

  // ─── Helper: start polling for discovery status ────────────────────
  const startPolling = useCallback(
    (skipToSelection: boolean) => {
      pollRef.current = setInterval(async () => {
        if (abortRef.current) {
          if (pollRef.current) clearInterval(pollRef.current);
          return;
        }

        try {
          const status: EntityDiscoveryStatusResponse =
            await connectionsService.getDiscoveryStatus(connectionId);

          if (abortRef.current) return;

          setDiscoveryMessage(status.message);
          setDiscoveryEntityCount(status.entity_count);

          if (status.status === 'complete' && status.result) {
            abortRef.current = true;
            if (pollRef.current) clearInterval(pollRef.current);
            setGroups(status.result.groups || []);
            setPresets(status.result.presets || []);
            setRelationships(status.result.relationships || []);
            setDiscoveryWarning(status.result.warning?.message ?? null);
            const discovered = (status.result.groups || []).flatMap(group => group.entities);
            const route = routeDiscoveredEntities(discovered, autoSelectSingleEntity);
            if (route.kind === 'error') {
              setError(route.message);
              setPhase('selection');
              return;
            }
            if (route.kind === 'auto-select') {
              savingRef.current = true;
              setSaving(true);
              try {
                await connectionsService.saveEntities(connectionId, [{
                  entity_name: route.entity.name,
                  label: route.entity.label,
                  sync_mode: 'full',
                }]);
                onComplete();
              } catch (reason) {
                savingRef.current = false;
                setSaving(false);
                setError(reason instanceof Error ? reason.message : 'Failed to save table selection');
                setPhase('selection');
              }
              return;
            }
            const discoveredStrategy: PostgreSQLSyncStrategy = !initialSelected?.length
              && status.result.cdc?.compatible ? 'cdc' : initialSyncStrategy;
            setSyncStrategy(discoveredStrategy);
            setSelected(previous => applyPostgreSQLSyncStrategy(
              previous, discovered, discoveredStrategy, pollingDraftsRef.current,
            ));
            if (status.result.cdc) {
              setCDCDiscovery(status.result.cdc);
              setCDCConfig(current => current ?? {
                ...(status.result!.cdc!.configured ?? {
                  enabled: true,
                  slot_name: status.result!.cdc!.suggested_slot_name,
                  publication_name: status.result!.cdc!.suggested_publication_name,
                  create_slot: true,
                  create_publication: true,
                }),
              });
            }

            if (skipToSelection) {
              setPhase('selection');
            } else if (status.result.presets && status.result.presets.length > 0) {
              setPhase('intent');
            } else {
              setPhase('selection');
            }
          } else if (status.status === 'error') {
            abortRef.current = true;
            if (pollRef.current) clearInterval(pollRef.current);
            setError(status.error || 'Discovery failed');
            setPhase('selection');
          }
        } catch (err) {
          if (pollRef.current) clearInterval(pollRef.current);
          if (!abortRef.current) {
            setError(
              err instanceof Error ? err.message : 'Failed to check discovery status',
            );
            setPhase('selection');
          }
        }
      }, POLL_INTERVAL);
    },
    [autoSelectSingleEntity, connectionId, initialSelected, initialSyncStrategy, onComplete],
  );

  // ─── Start discovery + poll on mount ───────────────────────────────
  useEffect(() => {
    abortRef.current = false;
    autoRefreshAttempted.current = false;

    // Whether re-editing existing selections or fresh discovery,
    // always use the async pattern to avoid HTTP timeouts on slow connectors.
    const skipToSelection = !!(initialSelected && initialSelected.length > 0);

    connectionsService
      .discoverEntities(connectionId)
      .then(() => {
        if (abortRef.current) return;
        startPolling(skipToSelection);
      })
      .catch((err: Error) => {
        if (!abortRef.current) {
          setError(err.message || 'Failed to start discovery');
          setPhase('selection');
        }
      });

    return () => {
      abortRef.current = true;
      refreshRef.current?.abort();
      refreshRef.current = null;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [connectionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = useCallback(async (purpose: 'tables' | 'cdc' = 'tables') => {
    if (refreshRef.current || savingRef.current) return;
    const controller = new AbortController();
    refreshRef.current = controller;
    autoRefreshAttempted.current = true;
    setRefreshing(true);
    setRefreshPurpose(purpose);
    if (purpose === 'cdc') {
      setCDCVerificationError(null);
    } else {
      setRefreshError(null);
    }
    try {
      const result = await refreshEntityDiscovery(connectionId, connectionsService, controller.signal);
      if (controller.signal.aborted) return;
      const refreshed = result.groups.flatMap(group => group.entities);
      setSelected(previous => {
        if (syncStrategy === 'cdc') {
          pollingDraftsRef.current = reconcilePollingSelections(
            pollingDraftsRef.current, allEntities, refreshed,
          );
          return applyPostgreSQLSyncStrategy(previous, refreshed, 'cdc', pollingDraftsRef.current);
        }
        const reconciled = reconcilePollingSelections(previous, allEntities, refreshed);
        pollingDraftsRef.current = new Map(reconciled);
        return reconciled;
      });
      setGroups(result.groups);
      setPresets(result.presets || []);
      setRelationships(result.relationships || []);
      setDiscoveryWarning(result.warning?.message ?? null);
      if (result.cdc) {
        setCDCDiscovery(result.cdc);
      }
      if (purpose === 'cdc') {
        const feedback = postgreSQLCDCVerificationFeedback(result.cdc);
        setCDCVerificationError(feedback.error);
      } else if (needsPollingMetadataRefresh(refreshed)) {
        setRefreshError('Timestamp or row identifier details are still unavailable. Ask your administrator to check the connection services, then retry.');
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        const message = err instanceof Error ? err.message : 'Failed to refresh discovery';
        if (purpose === 'cdc') setCDCVerificationError(message);
        else setRefreshError(message);
      }
    } finally {
      if (refreshRef.current === controller) {
        refreshRef.current = null;
        setRefreshing(false);
        setRefreshPurpose(null);
      }
    }
  }, [connectionId, allEntities, saving, syncStrategy]);

  useEffect(() => {
    // One automatic recovery per editor session; persistent failures require an explicit retry.
    if (phase === 'selection' && !saving && !autoRefreshAttempted.current
        && needsPollingMetadataRefresh(allEntities)) {
      void handleRefresh();
    }
  }, [phase, allEntities, saving, handleRefresh]);

  // ─── Preset selection handler ──────────────────────────────────────
  const handlePresetSelect = useCallback(
    (preset: EntityPreset | null) => {
      if (preset) {
        const entityNameSet = new Set(preset.entity_names);
        setSelected(previous => {
          const newSelected = new Map<string, SelectedEntity>();
          for (const entity of allEntities) {
            if (entityNameSet.has(entity.name)) {
              newSelected.set(entity.name, previous.get(entity.name) ?? selectionForCurrentStrategy(entity));
            }
          }
          return newSelected;
        });
      }
      setPhase('selection');
    },
    [allEntities, selectionForCurrentStrategy],
  );

  // ─── Toggle group expand/collapse ──────────────────────────────────
  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  // ─── Toggle entity selection ───────────────────────────────────────
  const toggleEntity = useCallback(
    (entity: EntityDiscoveryItem) => {
      setSelected((prev) => {
        const next = new Map(prev);
        if (next.has(entity.name)) {
          next.delete(entity.name);
        } else {
          next.set(entity.name, selectionForCurrentStrategy(entity));
        }
        return next;
      });
    },
    [selectionForCurrentStrategy],
  );

  // ─── Select / deselect all in a group ──────────────────────────────
  const selectAllInGroup = useCallback(
    (entities: EntityDiscoveryItem[]) => {
      setSelected((prev) => {
        const next = new Map(prev);
        for (const entity of entities) {
          if (!next.has(entity.name)) {
            next.set(entity.name, selectionForCurrentStrategy(entity));
          }
        }
        return next;
      });
    },
    [selectionForCurrentStrategy],
  );

  const deselectAllInGroup = useCallback(
    (entities: EntityDiscoveryItem[]) => {
      setSelected((prev) => {
        const next = new Map(prev);
        for (const entity of entities) {
          next.delete(entity.name);
        }
        return next;
      });
    },
    [],
  );

  // ─── Filter entities by search ─────────────────────────────────────
  const filterEntities = useCallback(
    (entities: EntityDiscoveryItem[]): EntityDiscoveryItem[] => {
      if (!search.trim()) return entities;
      const q = search.toLowerCase();
      return entities.filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          e.label.toLowerCase().includes(q),
      );
    },
    [search],
  );

  // ─── Save ──────────────────────────────────────────────────────────
  const rowIssues = useMemo(() => entitySelectionIssues(selected, allEntities), [selected, allEntities]);
  const usesCDC = syncStrategy === 'cdc' && selected.size > 0;
  const unavailableSelections = useMemo(() => unavailableEntitySelections(selected, allEntities), [selected, allEntities]);

  useEffect(() => {
    if (!focusIssue) return;
    // Run after clearing search and expanding groups so the target is mounted.
    const row = rowRefs.current.get(focusIssue);
    row?.scrollIntoView({ block: 'nearest' });
    row?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
    setFocusIssue(null);
  }, [focusIssue]);

  const applyPollingDraft = useCallback((draft: SelectedEntity) => {
    pollingDraftsRef.current.set(draft.entity_name, draft);
    setSelected(previous => applyEntitySelectionDraft(previous, draft));
  }, []);

  const applySyncStrategy = useCallback((strategy: PostgreSQLSyncStrategy) => {
    if (strategy === syncStrategy) return;
    setSelected(previous => {
      if (syncStrategy === 'polling') {
        pollingDraftsRef.current = new Map(Array.from(previous)
          .filter(([, selection]) => selection.sync_mode !== 'cdc'));
      }
      return applyPostgreSQLSyncStrategy(previous, allEntities, strategy, pollingDraftsRef.current);
    });
    if (strategy === 'cdc') {
      setCDCConfig(current => current && ({ ...current, enabled: true }));
    }
    setSyncStrategy(strategy);
    setPendingSyncStrategy(null);
    setError(null);
  }, [allEntities, syncStrategy]);

  const requestSyncStrategy = useCallback((strategy: PostgreSQLSyncStrategy) => {
    if (strategy === syncStrategy) return;
    if (selected.size === 0) applySyncStrategy(strategy);
    else setPendingSyncStrategy(strategy);
  }, [applySyncStrategy, selected.size, syncStrategy]);

  const saveSelection = useCallback(async () => {
    // Lock synchronously so rapid clicks cannot submit twice before React rerenders.
    if (savingRef.current || refreshRef.current) throw new Error('Wait for the current save or table refresh to finish.');
    const payload = prepareEntitySelectionSave(selected, allEntities);
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      await connectionsService.saveEntities(
        connectionId,
        payload,
        usesCDC ? cdcConfig ?? undefined : undefined,
      );
      setSelected(new Map(payload.map(selection => [selection.entity_name, selection])));
      onComplete();
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [connectionId, selected, onComplete, allEntities, usesCDC, cdcConfig]);

  const handleSave = useCallback(async () => {
    if (savingRef.current || refreshRef.current) return;
    if (rowIssues.size) {
      setError(null);
      setSearch('');
      setExpandedGroups(previous => new Set([...previous,
        ...groups.filter(group => group.entities.some(entity => rowIssues.has(entity.name))).map(group => group.key)]));
      setFocusIssue(rowIssues.keys().next().value!);
      return;
    }
    try {
      await saveSelection();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save entity selection');
    }
  }, [saveSelection, rowIssues, groups]);

  // ─── Total entity count ────────────────────────────────────────────
  const totalEntities = useMemo(
    () => groups.reduce((sum, g) => sum + g.entities.length, 0),
    [groups],
  );

  // =====================================================================
  // Render
  // =====================================================================

  const cancelButton = onCancel ? (
    <Button appearance="secondary" onClick={onCancel} disabled={saving}>
      Cancel
    </Button>
  ) : null;

  // ─── Discovering Phase ─────────────────────────────────────────────
  if (phase === 'discovering') {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spinner size="medium" />
        <div style={{ marginTop: 16 }}>
          <Text weight="semibold" size={400} style={{ display: 'block' }}>
            {discoveryMessage}
          </Text>
          {discoveryEntityCount > 0 && (
            <Text
              size={200}
              style={{
                display: 'block',
                color: tokens.colorNeutralForeground3,
                marginTop: 4,
              }}
            >
              Found {discoveryEntityCount} tables so far
            </Text>
          )}
        </div>
        {cancelButton && <div style={{ marginTop: 16 }}>{cancelButton}</div>}
      </div>
    );
  }

  if (error && groups.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</Text>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <Button appearance="primary" disabled={refreshing} onClick={() => void handleRefresh()}>
            {refreshing ? 'Retrying…' : 'Retry table discovery'}
          </Button>
          {cancelButton}
        </div>
      </div>
    );
  }

  // ─── Phase: Intent Selection ───────────────────────────────────────
  if (phase === 'intent' && presets.length > 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 600 }}>
        <div>
          <Text size={500} weight="semibold" style={{ display: 'block', marginBottom: 8 }}>
            What would you like to analyze?
          </Text>
          <Text
            size={300}
            style={{ color: tokens.colorNeutralForeground3 }}
          >
            Choose a starting point for your data model. You can customize tables in the next step.
          </Text>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {presets.map((preset) => (
            <Card
              key={preset.key}
              style={{
                padding: '16px 20px',
                cursor: 'pointer',
                border: `1px solid ${tokens.colorNeutralStroke2}`,
                borderRadius: tokens.borderRadiusMedium,
              }}
              onClick={() => handlePresetSelect(preset)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <DatabaseRegular style={{ fontSize: 24, color: tokens.colorBrandForeground1 }} />
                <div style={{ flex: 1 }}>
                  <Text weight="semibold" size={400}>
                    {preset.label}
                  </Text>
                  {preset.description && (
                    <Text
                      size={200}
                      style={{
                        display: 'block',
                        color: tokens.colorNeutralForeground3,
                        marginTop: 2,
                      }}
                    >
                      {preset.description}
                    </Text>
                  )}
                </div>
                <Badge appearance="outline" size="small">
                  {preset.entity_names.length} tables
                </Badge>
              </div>
            </Card>
          ))}

          {/* Custom Selection option */}
          <Card
            style={{
              padding: '16px 20px',
              cursor: 'pointer',
              border: `1px solid ${tokens.colorNeutralStroke2}`,
              borderRadius: tokens.borderRadiusMedium,
            }}
            onClick={() => handlePresetSelect(null)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <OptionsRegular style={{ fontSize: 24, color: tokens.colorNeutralForeground3 }} />
              <div style={{ flex: 1 }}>
                <Text weight="semibold" size={400}>
                  Custom Selection
                </Text>
                <Text
                  size={200}
                  style={{
                    display: 'block',
                    color: tokens.colorNeutralForeground3,
                    marginTop: 2,
                  }}
                >
                  Browse all available tables and build your own selection
                </Text>
              </div>
              <Badge appearance="outline" size="small">
                {totalEntities} tables
              </Badge>
            </div>
          </Card>
        </div>
        {cancelButton}
      </div>
    );
  }

  // ─── Phase: Structured Entity Selection ────────────────────────────
  return (
    <div className="entity-selection">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Text size={500} weight="semibold">
            Select Tables
          </Text>
          <Text
            size={200}
            style={{ display: 'block', color: tokens.colorNeutralForeground3, marginTop: 4 }}
          >
            Choose which tables to sync. At least one table must be selected.
          </Text>
        </div>
        <Badge appearance="filled" color="brand">
          {selected.size} selected
        </Badge>
      </div>

      {/* Back button if presets exist */}
      {presets.length > 0 && (
        <Button
          size="small"
          appearance="subtle"
          onClick={() => setPhase('intent')}
        >
          ← Change analysis type
        </Button>
      )}

      {discoveryWarning && (
        <MessageBar intent="warning" role="status">
          <MessageBarBody>{discoveryWarning}</MessageBarBody>
        </MessageBar>
      )}

      {cdcDiscovery && (
        <Card className="postgresql-sync-strategy">
          <Field label="Sync strategy" hint={syncStrategy === 'cdc'
            ? 'Reads committed inserts, updates, and deletes from PostgreSQL logical replication. Every selected table must have a replica identity.'
            : 'Polls each table using a stable row identifier and update timestamp; a look-back captures late commits, but hard deletes require a full snapshot.'}>
            <RadioGroup value={syncStrategy} layout="horizontal"
              onChange={(_, data) => requestSyncStrategy(data.value as PostgreSQLSyncStrategy)}
              disabled={saving || refreshing}>
              <Radio value="cdc" label="Change data capture (CDC) — Recommended" />
              <Radio value="polling" label="Incremental updates" />
            </RadioGroup>
          </Field>
          {syncStrategy === 'cdc' && (
            <div className="postgresql-sync-strategy-content">
              <PostgreSQLCDCSetupPanel discovery={cdcDiscovery}
                verifying={refreshing && refreshPurpose === 'cdc'}
                verificationError={cdcVerificationError}
                onVerify={() => void handleRefresh('cdc')} />
              {cdcConfig && (
                <details className="postgresql-cdc-config">
                  <summary>Replication settings</summary>
                  <div className="postgresql-cdc-config-content">
                    <Field label="Replication slot" hint="Choose an inactive compatible slot or create a dedicated slot.">
                      <Select value={cdcConfig.create_slot ? '__new__' : cdcConfig.slot_name}
                        disabled={saving || refreshing || !cdcDiscovery.compatible}
                        onChange={(_, data) => setCDCConfig(current => current && ({ ...current,
                          create_slot: data.value === '__new__',
                          slot_name: data.value === '__new__' ? cdcDiscovery.suggested_slot_name : data.value }))}>
                        <option value="__new__">Create dedicated slot: {cdcDiscovery.suggested_slot_name}</option>
                        {cdcDiscovery.slots.filter(slot => slot.compatible).map(slot =>
                          <option key={slot.name} value={slot.name}>{slot.name}</option>)}
                      </Select>
                    </Field>
                    {cdcConfig.create_slot && <Field label="New slot name">
                      <Input value={cdcConfig.slot_name} pattern="[a-z_][a-z0-9_]{0,62}"
                        disabled={saving || refreshing || !cdcDiscovery.compatible}
                        onChange={(_, data) => setCDCConfig(current => current && ({ ...current, slot_name: data.value }))} />
                    </Field>}
                    <details className="polling-disclosure">
                      <summary>Advanced publication settings</summary>
                      <div className="polling-options">
                        <Field label="Publication" hint="A dedicated table-scoped publication is recommended.">
                          <Select value={cdcConfig.create_publication ? '__new__' : cdcConfig.publication_name}
                            disabled={saving || refreshing || !cdcDiscovery.compatible}
                            onChange={(_, data) => setCDCConfig(current => current && ({ ...current,
                              create_publication: data.value === '__new__',
                              publication_name: data.value === '__new__' ? cdcDiscovery.suggested_publication_name : data.value }))}>
                            <option value="__new__">Create dedicated publication: {cdcDiscovery.suggested_publication_name}</option>
                            {cdcDiscovery.publications.filter(publication => publication.owned).map(publication =>
                              <option key={publication.name} value={publication.name}>{publication.name}</option>)}
                          </Select>
                        </Field>
                        {cdcConfig.create_publication && <Field label="New publication name">
                          <Input value={cdcConfig.publication_name} pattern="[a-z_][a-z0-9_]{0,62}"
                            disabled={saving || refreshing || !cdcDiscovery.compatible}
                            onChange={(_, data) => setCDCConfig(current => current && ({ ...current, publication_name: data.value }))} />
                        </Field>}
                      </div>
                    </details>
                    <Text size={200}>Replication slots retain WAL until acknowledged. Monitor slot lag and remove unused slots manually.</Text>
                  </div>
                </details>
              )}
            </div>
          )}
        </Card>
      )}

      <Dialog open={pendingSyncStrategy !== null}
        onOpenChange={(_, data) => { if (!data.open) setPendingSyncStrategy(null); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Change sync strategy?</DialogTitle>
            <DialogContent>
              <Text>
                {pendingSyncStrategy === 'cdc'
                  ? 'Per-table incremental and full snapshot settings will not be used while CDC is selected. They will be preserved if you switch back.'
                  : 'CDC slot and publication settings will not be used while incremental updates are selected. Per-table polling settings will be restored where possible.'}
              </Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setPendingSyncStrategy(null)}>Keep</Button>
              <Button appearance="primary" onClick={() => pendingSyncStrategy && applySyncStrategy(pendingSyncStrategy)}>Change</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Unavailable choices must remain visible even when search hides other rows. */}
      {unavailableSelections.length > 0 && (
        <section aria-label="Unavailable selected tables">
          <Text weight="semibold">Unavailable selected tables</Text>
          <Text as="p">Remove these selections or refresh table details before saving.</Text>
          {unavailableSelections.map(selection => (
            <div key={selection.entity_name} ref={node => {
              if (node) rowRefs.current.set(selection.entity_name, node);
              else rowRefs.current.delete(selection.entity_name);
            }}>
              <Text>{selection.label || selection.entity_name}</Text>
              <Button disabled={saving || refreshing}
                aria-label={`Remove unavailable table ${selection.label || selection.entity_name}`}
                onClick={() => setSelected(previous => {
                  const next = new Map(previous);
                  next.delete(selection.entity_name);
                  return next;
                })}>Remove</Button>
            </div>
          ))}
          <Button disabled={saving || refreshing} onClick={() => void handleRefresh()}>Refresh table details</Button>
        </section>
      )}

      {/* Search */}
      <Input
        placeholder="Search tables…"
        contentBefore={<SearchRegular />}
        value={search}
        onChange={(_, data) => setSearch(data.value)}
      />

      {/* Grouped entity list — all groups always shown */}
      <div
        className="entity-selection-list"
        style={{
          border: `1px solid ${tokens.colorNeutralStroke2}`,
          borderRadius: tokens.borderRadiusMedium,
        }}
      >
        {groups.map((group) => {
          const filteredEntities = filterEntities(group.entities);
          if (search.trim() && filteredEntities.length === 0) return null;

          const isExpanded = expandedGroups.has(group.key) || !!search.trim();
          const selectedCount = group.entities.filter((e) => selected.has(e.name)).length;
          const allSelected = selectedCount === group.entities.length && group.entities.length > 0;

          return (
            <div key={group.key}>
              {/* Group header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 12px',
                  cursor: 'pointer',
                  background: tokens.colorNeutralBackground3,
                  borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
                  userSelect: 'none',
                }}
                onClick={() => toggleGroup(group.key)}
              >
                {isExpanded ? (
                  <ChevronDownRegular style={{ fontSize: 16 }} />
                ) : (
                  <ChevronRightRegular style={{ fontSize: 16 }} />
                )}
                <Text weight="semibold" size={300} style={{ flex: 1 }}>
                  {group.label}
                </Text>
                <Badge
                  appearance="outline"
                  size="small"
                  color={selectedCount > 0 ? 'brand' : 'informative'}
                >
                  {selectedCount}/{group.entities.length}
                </Badge>
                <Button
                  size="small"
                  appearance="subtle"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (allSelected) {
                      deselectAllInGroup(group.entities);
                    } else {
                      selectAllInGroup(group.entities);
                    }
                  }}
                  style={{ minWidth: 'auto', padding: '2px 8px' }}
                >
                  {allSelected ? 'Clear' : 'All'}
                </Button>
              </div>

              {/* Entities in group */}
              {isExpanded && (
                <div>
                  {filteredEntities.length === 0 && (
                    <div style={{ padding: 16, textAlign: 'center' }}>
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                        No matching tables
                      </Text>
                    </div>
                  )}
                  {filteredEntities.map((entity) => {
                    const isSelected = selected.has(entity.name);

                    return (
                      <div
                        key={entity.name}
                        ref={node => {
                          // Non-queryable selections use the visible removal control above.
                          if (!entity.is_queryable) return;
                          if (node) rowRefs.current.set(entity.name, node);
                          else rowRefs.current.delete(entity.name);
                        }}
                      >
                      <div
                        className="entity-selection-row"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '8px 12px 8px 36px',
                          borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
                          background: isSelected
                            ? tokens.colorNeutralBackground1Selected
                            : undefined,
                          cursor: 'pointer',
                        }}
                        onClick={() => setInspectedEntity(entity)}
                        role="button"
                        tabIndex={0}
                        aria-label={`Inspect ${entity.label}`}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setInspectedEntity(entity);
                          }
                        }}
                      >
                        <Checkbox
                          aria-label={`Select ${entity.label}`}
                          checked={isSelected}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleEntity(entity);
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text weight="semibold" size={300}>
                            {entity.label}
                          </Text>
                          {!(entity.source_schema && entity.source_table) && <Text
                            size={200}
                            style={{
                              display: 'block',
                              color: tokens.colorNeutralForeground3,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {entity.name}
                          </Text>}
                        </div>
                        {isSelected && entity.polling && syncStrategy === 'polling' && (
                          <PostgreSQLPollingOptions entity={entity} selection={selected.get(entity.name)!}
                            validationMessage={rowIssues.get(entity.name)}
                            disabled={saving || refreshing} refreshing={refreshing} refreshError={refreshError}
                            onRefresh={() => void handleRefresh()} onDone={applyPollingDraft} />
                        )}
                        {isSelected && entity.polling && syncStrategy === 'cdc' && (
                          <Text size={200} className="postgresql-cdc-table-summary"
                            style={{ color: rowIssues.has(entity.name) ? tokens.colorPaletteRedForeground1 : undefined }}>
                            {entity.polling.replica_identity_columns?.length
                              ? `CDC · Replica identity: ${entity.polling.replica_identity_columns.join(', ')}`
                              : 'CDC unavailable · Replica identity required'}
                          </Text>
                        )}
                        {isSelected && !entity.polling && normalizedRetention && (
                          <IncrementalRetentionOptions selection={selected.get(entity.name)!}
                            policy={normalizedRetention} disabled={saving || refreshing}
                            onChange={selection => setSelected(previous => {
                              const next = new Map(previous);
                              next.set(entity.name, selection);
                              return next;
                            })} />
                        )}
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
                          {entity.is_custom && (
                            <Badge appearance="outline" size="small">
                              Custom
                            </Badge>
                          )}
                          {entity.relationships_count !== undefined && entity.relationships_count > 0 && (
                            <Badge appearance="tint" size="small" color="informative">
                              {entity.relationships_count} relationships
                            </Badge>
                          )}
                        </div>
                        {isSelected && (
                          <CheckmarkCircleRegular
                            style={{ color: tokens.colorPaletteGreenForeground1, flexShrink: 0 }}
                          />
                        )}
                      </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <TableInspectorDrawer
        connectionId={connectionId}
        entity={inspectedEntity}
        relatedEntities={inspectedEntity ? relationshipIndex.get(inspectedEntity.name) ?? [] : []}
        selectedNames={new Set(selected.keys())}
        onClose={() => setInspectedEntity(null)}
        onToggleSelection={toggleEntity}
      />

      {/* Error */}
      {((refreshing && refreshPurpose === 'tables') || refreshError) && (
        <div role="status">
          <Text size={200}>{refreshing && refreshPurpose === 'tables' ? 'Refreshing table details… Your unsaved selections are retained.' : refreshError}</Text>
          {refreshError && <Button disabled={saving || refreshing} onClick={() => void handleRefresh()}>Retry table details</Button>}
        </div>
      )}
      {error && (
        <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>
          {error}
        </Text>
      )}

      {/* Save and cancel actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button
          appearance="primary"
          disabled={selected.size === 0 || saving || refreshing || (usesCDC && cdcDiscovery?.compatible !== true)}
          onClick={handleSave}
          icon={saving ? <Spinner size="tiny" /> : undefined}
        >
          {saving ? 'Saving…' : `Save Selection (${selected.size})`}
        </Button>
        {cancelButton}
      </div>
    </div>
  );
};

export default EntitySelection;
